import { ReactElement } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { DenseTable } from '@ui2/structure'
import { type ImportPlanCard, formatMetres } from '../utils'
import DecisionButtons, { ChangeDecision } from './DecisionButtons'

interface Props {
  plan: ImportPlanCard
  decision: ChangeDecision | undefined
  onDecide: (approved: boolean) => void
}

/**
 * The agent's import plan: storeys, element counts and steps, to approve before it starts. With an
 * assignment (types, storeys and filter already set in the IFC view), approving it confirms that
 * configuration as checked in the scene.
 */
const ImportPlanItem = ({ plan, decision, onDecide }: Props): ReactElement => (
  <Box
    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
    data-cy="ai-chat-import-plan"
  >
    <Typography variant="body2" fontWeight={500}>
      {plan.summary}
    </Typography>
    <Stack spacing={1} mt={1}>
      <DenseTable
        headers={['Stockwerk', 'Unterkante', 'Oberkante']}
        rows={plan.storeys.map(storey => [
          storey.name,
          formatMetres(storey.lowerM),
          formatMetres(storey.upperM),
        ])}
        emptyMessage="Keine Stockwerke angegeben"
      />
      {plan.counts.length > 0 && (
        <DenseTable
          headers={['Element', 'Anzahl']}
          rows={plan.counts.map(([kind, count]) => [kind, count])}
          emptyMessage=""
        />
      )}
      {plan.steps.length > 0 && (
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {plan.steps.map((step, index) => (
            <Typography component="li" variant="caption" key={index}>
              {step}
            </Typography>
          ))}
        </Box>
      )}
      {plan.assignment.length > 0 && (
        <Stack spacing={0.5} data-cy="ai-chat-import-plan-assignment">
          <Typography variant="caption" fontWeight={500}>
            Zuordnung in der Szene prüfen
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Typen, Stockwerke und Filter sind im IFC-Modell gesetzt wie im geführten Import. Ist das
            die richtige Konfiguration? Korrekturen im geführten Import werden beim Freigeben
            übernommen; oder ablehnen und im Chat sagen, was anders sein soll.
          </Typography>
          <DenseTable
            headers={['Zuordnung', 'Anzahl']}
            rows={plan.assignment.map(([kind, count]) => [kind, count])}
            emptyMessage=""
          />
        </Stack>
      )}
      {plan.notes && (
        <Typography variant="caption" color="text.secondary">
          {plan.notes}
        </Typography>
      )}
    </Stack>
    <DecisionButtons decision={decision} onDecide={onDecide} />
  </Box>
)

export default ImportPlanItem
